import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
  AlertTriangle,
  BarChart3,
  Boxes,
  Building2,
  PackageX,
  ShoppingCart,
  Store,
  Wallet,
  Layers,
  type LucideIcon,
} from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { TD_DENSITY, TH_DENSITY } from '@/components/ui/tableDensity';
import { getSisDashboard, type SisDashboard } from '@/api/sisStock';
import { formatINR } from '@/lib/format';

interface KpiCfg {
  key: keyof SisDashboard['kpis'];
  label: string;
  Icon: LucideIcon;
  accent: string;
  bg: string;
  fmt: (v: number) => number | string;
}

const KPI_CONFIG: KpiCfg[] = [
  { key: 'groups',       label: 'Groups',        Icon: Building2,     accent: '#ec4899', bg: 'rgba(236,72,153,0.10)',  fmt: (v) => v },
  { key: 'stores',       label: 'SIS Stores',    Icon: Store,         accent: '#FF8C00', bg: 'rgba(255,140,0,0.10)',   fmt: (v) => v },
  { key: 'itemsTracked', label: 'Items Tracked', Icon: Layers,        accent: '#6366f1', bg: 'rgba(99,102,241,0.10)',  fmt: (v) => v },
  { key: 'unitsOnHand',  label: 'Units On Hand', Icon: Boxes,         accent: '#3b82f6', bg: 'rgba(59,130,246,0.10)',  fmt: (v) => v },
  { key: 'stockValue',   label: 'Stock Value',   Icon: Wallet,        accent: '#10b981', bg: 'rgba(16,185,129,0.10)',  fmt: formatINR },
  { key: 'unitsSold',    label: 'Units Sold',    Icon: ShoppingCart,  accent: '#8b5cf6', bg: 'rgba(139,92,246,0.10)',  fmt: (v) => v },
  { key: 'lowStock',     label: 'Low Stock',     Icon: AlertTriangle, accent: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  fmt: (v) => v },
  { key: 'outOfStock',   label: 'Out of Stock',  Icon: PackageX,      accent: '#ef4444', bg: 'rgba(239,68,68,0.10)',   fmt: (v) => v },
];

const compact = (n: number) =>
  Math.abs(n) >= 1_00_00_000 ? `₹${(n / 1_00_00_000).toFixed(2)}Cr`
  : Math.abs(n) >= 1_00_000 ? `₹${(n / 1_00_000).toFixed(2)}L`
  : Math.abs(n) >= 1_000 ? `₹${(n / 1_000).toFixed(1)}K`
  : `₹${Math.round(n)}`;
const pct = (v: number, total: number) => (total > 0 && v > 0 ? `${((v / total) * 100).toFixed(v / total < 0.1 ? 1 : 0)}%` : '0%');

// ERP theme colours. Groups take a colour by name order, the same as on SIS Analytics.
const GROUP_COLORS = ['#3B82F6', '#FF8C00', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4'];
const HEALTHY = '#10B981';
const LOW = '#F59E0B';
const OUT = '#EF4444';

type StoreMetric = 'stockValue' | 'unitsOnHand' | 'saleAmount' | 'unitsSold';
const STORE_METRICS: { key: StoreMetric; label: string; fmt: (n: number) => string }[] = [
  { key: 'stockValue', label: 'Stock value', fmt: (n) => `₹${formatINR(n)}` },
  { key: 'unitsOnHand', label: 'Units on hand', fmt: (n) => String(n) },
  { key: 'saleAmount', label: 'Sale amount', fmt: (n) => `₹${formatINR(n)}` },
  { key: 'unitsSold', label: 'Units sold', fmt: (n) => String(n) },
];

const COLS = ['Store', 'Items', 'Units On Hand', 'Stock Value', 'Units Sold', 'Sale Amount', 'Low', 'Out'];
const GROUP_COLS = ['Group', 'Stores', 'Units On Hand', 'Stock Value', 'Units Sold', 'Sale Amount', 'Low', 'Out'];

export function SisDashboardPage() {
  // All / Date (from–to) / Month / Quarter / Year — scopes sales only.
  const period = usePeriod();
  const { range } = period;
  const from = range?.from ?? '';
  const to = range?.to ?? '';
  const q = useQuery({
    queryKey: ['sis-dashboard', from, to],
    queryFn: () => getSisDashboard({ from: from || undefined, to: to || undefined }),
    refetchInterval: 60_000,
  });
  const kpis = q.data?.kpis;
  const rows = q.data?.perStore ?? [];
  const groups = q.data?.perGroup ?? [];
  const periodOn = !!(from || to);

  // ── Chart data (derived from the same response the tables use) ──
  const [metric, setMetric] = useState<StoreMetric>('stockValue');
  const [hoverSeg, setHoverSeg] = useState<number | null>(null);
  const groupNames = useMemo(() => groups.map((g) => g.group).sort(), [groups]);
  const groupColor = (g: string) => { const i = groupNames.indexOf(g); return i >= 0 && i < GROUP_COLORS.length ? GROUP_COLORS[i] : '#94A3B8'; };
  const m = STORE_METRICS.find((x) => x.key === metric)!;
  const ranked = useMemo(() => [...rows].sort((a, b) => b[metric] - a[metric]), [rows, metric]);
  const maxMetric = Math.max(1, ...rows.map((r) => r[metric]));
  const valueGroups = useMemo(() => groups.filter((g) => g.stockValue > 0).sort((a, b) => b.stockValue - a.stockValue), [groups]);
  const valueTotal = valueGroups.reduce((a, g) => a + g.stockValue, 0);
  const hovered = hoverSeg == null ? undefined : valueGroups[hoverSeg];
  // Stock health: Low and Out never overlap (Low means some stock, at or under the reorder level).
  const attention = useMemo(
    () => rows.filter((r) => r.lowStock + r.outOfStock > 0).sort((a, b) => b.lowStock + b.outOfStock - (a.lowStock + a.outOfStock)),
    [rows],
  );
  const maxAttention = Math.max(1, ...attention.map((r) => r.lowStock + r.outOfStock));
  const tracked = rows.reduce((a, r) => a + r.itemsTracked, 0);
  const lowTotal = rows.reduce((a, r) => a + r.lowStock, 0);
  const outTotal = rows.reduce((a, r) => a + r.outOfStock, 0);
  const health = [
    { label: 'Healthy', value: Math.max(0, tracked - lowTotal - outTotal), color: HEALTHY },
    { label: 'Low stock', value: lowTotal, color: LOW },
    { label: 'Out of stock', value: outTotal, color: OUT },
  ];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="SIS — Module Overview"      />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {KPI_CONFIG.map(({ key, label, Icon, accent, fmt }) => {
          const raw = kpis?.[key];
          const val = raw !== undefined ? fmt(raw) : null;
          return (
            <div
              key={key}
              className="relative overflow-hidden rounded-xl p-4 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ background: accent, textShadow: KPI_TEXT_SHADOW }}
            >
              <Icon className="absolute -bottom-3 -right-3 h-20 w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
              <div className="relative">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">
                  {label}
                </span>
                <div className={`mt-2 text-2xl font-extrabold ${val === null ? 'text-white/70' : ''}`}>
                  {val === null
                    ? q.isLoading
                      ? <span className="inline-block h-6 w-16 animate-pulse rounded" style={{ background: 'rgba(255,255,255,0.3)' }} />
                      : '—'
                    : val}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compact period filter — scopes Units Sold & Sale Amount; stock is live. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 backdrop-blur-xl">
        <span className="text-[11px] font-black uppercase tracking-wide text-accent">Period</span>
        <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        <span className="ml-auto text-[10px] text-[var(--text-muted)]">
          {periodOn ? 'Units Sold & Sale Amount for the selected period · stock is live on-hand' : 'All-time sales · pick a range to scope · stock is live on-hand'}
        </span>
      </div>

      {/* Charts — the tables below hold the full figures */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ChartCard
          className="md:col-span-2"
          title="Store comparison"
          icon={BarChart3}
          action={
            <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
              {STORE_METRICS.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  onClick={() => setMetric(x.key)}
                  className={'rounded-md px-2 py-1 text-[11px] font-bold transition ' + (metric === x.key ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')}
                >
                  {x.label}
                </button>
              ))}
            </div>
          }
        >
          {q.isLoading ? <ChartSkeleton /> : ranked.length === 0 ? <ChartEmpty text="No SIS stock yet." /> : (
            <>
              <div className="space-y-1.5">
                {ranked.map((r) => (
                  <div key={r.partyId} className="flex items-center gap-3 text-xs" title={`${r.storeName} — ₹${formatINR(r.stockValue)} stock · ${r.unitsOnHand} units on hand · ${r.unitsSold} sold · ₹${formatINR(r.saleAmount)} sales`}>
                    <span className="w-32 shrink-0 truncate font-bold text-[var(--text-primary)] sm:w-44">{r.storeName}</span>
                    <div className="h-2.5 min-w-0 flex-1 rounded-r-full bg-[var(--bg-soft)]">
                      {r[metric] > 0 && <div className="h-full rounded-r-full" style={{ width: `${(r[metric] / maxMetric) * 100}%`, background: groupColor(r.group) }} />}
                    </div>
                    <span className="w-24 shrink-0 text-right font-bold text-[var(--text-primary)]">{m.fmt(r[metric])}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--border-color)] pt-2.5 text-[11px] text-[var(--text-secondary)]">
                {groupNames.map((g) => (
                  <span key={g} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: groupColor(g) }} />{g}</span>
                ))}
              </div>
            </>
          )}
        </ChartCard>

        <ChartCard title="Stock value by group" icon={Building2}>
          {q.isLoading ? <ChartSkeleton /> : valueGroups.length === 0 ? <ChartEmpty text="No stock value yet." /> : (
            <>
              <div className="relative mx-auto h-36 w-36" onMouseLeave={() => setHoverSeg(null)}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={valueGroups} dataKey="stockValue" nameKey="group" innerRadius={46} outerRadius={66} paddingAngle={valueGroups.length > 1 ? 2 : 0} stroke="none"
                      startAngle={90} endAngle={-270} isAnimationActive={false}
                      onMouseEnter={(_, i) => setHoverSeg(i)} onMouseLeave={() => setHoverSeg(null)}
                    >
                      {valueGroups.map((g, i) => <Cell key={g.group} fill={groupColor(g.group)} fillOpacity={hoverSeg == null || hoverSeg === i ? 1 : 0.35} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Centre readout: the total, or the hovered group. */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-extrabold text-[var(--text-primary)]">{compact(hovered ? hovered.stockValue : valueTotal)}</span>
                  <span className="max-w-[80px] truncate text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{hovered ? hovered.group : 'Stock value'}</span>
                  {hovered && <span className="text-[10px] font-semibold text-[var(--text-secondary)]">{pct(hovered.stockValue, valueTotal)}</span>}
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {valueGroups.map((g) => (
                  <div key={g.group} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: groupColor(g.group) }} />
                      <span className="truncate">{g.group}</span>
                      <span className="shrink-0 text-[10px] text-[var(--text-muted)]">{g.stores} store{g.stores === 1 ? '' : 's'}</span>
                    </span>
                    <span className="shrink-0 text-[var(--text-muted)]"><strong className="text-[var(--text-primary)]">{compact(g.stockValue)}</strong> · {pct(g.stockValue, valueTotal)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ChartCard className="md:col-span-2" title="Items needing attention by store" icon={AlertTriangle}>
          {q.isLoading ? <ChartSkeleton /> : attention.length === 0 ? <ChartEmpty text="No low or out-of-stock items at any store." /> : (
            <>
              <div className="space-y-1.5">
                {attention.map((r) => (
                  <div key={r.partyId} className="flex items-center gap-3 text-xs" title={`${r.storeName} — ${r.lowStock} low · ${r.outOfStock} out of ${r.itemsTracked} items`}>
                    <span className="w-32 shrink-0 truncate font-bold text-[var(--text-primary)] sm:w-44">{r.storeName}</span>
                    <div className="flex h-2.5 min-w-0 flex-1 gap-[2px]">
                      {r.lowStock > 0 && <div className="h-full rounded-sm" style={{ width: `${(r.lowStock / maxAttention) * 100}%`, background: LOW }} />}
                      {r.outOfStock > 0 && <div className="h-full rounded-sm" style={{ width: `${(r.outOfStock / maxAttention) * 100}%`, background: OUT }} />}
                    </div>
                    <span className="w-28 shrink-0 text-right text-[var(--text-secondary)]">
                      <strong className="text-[var(--text-primary)]">{r.lowStock}</strong> low · <strong className="text-[var(--text-primary)]">{r.outOfStock}</strong> out
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--border-color)] pt-2.5 text-[11px] text-[var(--text-secondary)]">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: LOW }} />Low stock (at or under reorder level)</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: OUT }} />Out of stock</span>
              </div>
            </>
          )}
        </ChartCard>

        <ChartCard title="Stock health" icon={Boxes}>
          {q.isLoading ? <ChartSkeleton /> : tracked === 0 ? <ChartEmpty text="No items tracked yet." /> : (
            <>
              <div className="text-xs text-[var(--text-secondary)]"><strong className="text-xl text-[var(--text-primary)]">{pct(health[0].value, tracked)}</strong> of {tracked} item lines are healthy</div>
              <div className="mt-2 flex h-3 w-full gap-[2px] overflow-hidden rounded-full">
                {health.filter((h) => h.value > 0).map((h) => (
                  <div key={h.label} style={{ width: `${(h.value / tracked) * 100}%`, background: h.color }} title={`${h.label}: ${h.value} (${pct(h.value, tracked)})`} />
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                {health.map((h) => (
                  <div key={h.label} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-1.5 text-[var(--text-secondary)]"><span className="h-2 w-2 rounded-full" style={{ background: h.color }} />{h.label}</span>
                    <span className="text-[var(--text-muted)]"><strong className="text-[var(--text-primary)]">{h.value}</strong> · {pct(h.value, tracked)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>
      </div>

      {/* Per-Group Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
        <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
          <span className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>By Group / Company</span>
          <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold" style={{ background: 'var(--accent-muted, rgba(255,140,0,0.12))', color: 'var(--accent)' }}>
            {groups.length} group{groups.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                {GROUP_COLS.map((h, i) => (
                  <th key={h} className={`border-b-2 border-r border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wider text-accent whitespace-nowrap last:border-r-0 ${TH_DENSITY}`} style={{ textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.isLoading && groups.length === 0 ? (
                <tr>{GROUP_COLS.map((_, j) => (
                  <td key={j} className={`border-r border-[var(--border-color)] last:border-r-0 ${TD_DENSITY}`}><span className="inline-block h-4 w-full animate-pulse rounded" style={{ background: 'var(--border-color)' }} /></td>
                ))}</tr>
              ) : groups.length === 0 ? (
                <tr><td colSpan={GROUP_COLS.length} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No groups yet.</td></tr>
              ) : (
                groups.map((g, idx) => (
                  <tr key={g.group} style={{ borderBottom: idx < groups.length - 1 ? '1px solid var(--border-color)' : 'none', background: idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)' }}>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 font-bold ${TD_DENSITY}`} style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />{g.group}</div>
                    </td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{g.stores}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{g.unitsOnHand}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-semibold ${TD_DENSITY}`} style={{ color: 'var(--text-primary)' }}>{formatINR(g.stockValue)}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{g.unitsSold}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-semibold ${TD_DENSITY}`} style={{ color: 'var(--text-primary)' }}>{formatINR(g.saleAmount)}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-bold ${TD_DENSITY}`} style={{ color: g.lowStock > 0 ? '#f59e0b' : 'var(--text-muted)' }}>{g.lowStock}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-bold ${TD_DENSITY}`} style={{ color: g.outOfStock > 0 ? '#ef4444' : 'var(--text-muted)' }}>{g.outOfStock}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Store Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
        <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
          <span className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>Per-Store Summary</span>
          <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold" style={{ background: 'var(--accent-muted, rgba(255,140,0,0.12))', color: 'var(--accent)' }}>
            {rows.length} store{rows.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                {COLS.map((h, i) => (
                  <th
                    key={h}
                    className={`border-b-2 border-r border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wider text-accent whitespace-nowrap last:border-r-0 ${TH_DENSITY}`}
                    style={{ textAlign: i === 0 ? 'left' : 'right' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.isLoading && rows.length === 0 ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    {COLS.map((__, j) => (
                      <td key={j} className={`border-r border-[var(--border-color)] last:border-r-0 ${TD_DENSITY}`}>
                        <span className="inline-block h-4 w-full animate-pulse rounded" style={{ background: 'var(--border-color)' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    No SIS stock yet — dispatch to an MBO &amp; SiS customer to populate this.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr
                    key={r.partyId}
                    style={{
                      borderBottom: idx < rows.length - 1 ? '1px solid var(--border-color)' : 'none',
                      background: idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)',
                    }}
                  >
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 font-semibold ${TD_DENSITY}`} style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
                        {r.storeName}{r.city ? <span style={{ color: 'var(--text-muted)' }}> · {r.city}</span> : null}
                      </div>
                    </td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{r.itemsTracked}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{r.unitsOnHand}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{formatINR(r.stockValue)}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right ${TD_DENSITY}`} style={{ color: 'var(--text-secondary)' }}>{r.unitsSold}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-semibold ${TD_DENSITY}`} style={{ color: 'var(--text-primary)' }}>{formatINR(r.saleAmount)}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-bold ${TD_DENSITY}`} style={{ color: r.lowStock > 0 ? '#f59e0b' : 'var(--text-muted)' }}>{r.lowStock}</td>
                    <td className={`border-r border-[var(--border-color)] last:border-r-0 text-right font-bold ${TD_DENSITY}`} style={{ color: r.outOfStock > 0 ? '#ef4444' : 'var(--text-muted)' }}>{r.outOfStock}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, action, className = '', children }: {
  title: string; icon: LucideIcon; action?: ReactNode; className?: string; children: ReactNode;
}) {
  return (
    <div className={`overflow-hidden rounded-xl ${className}`} style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2" style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <span className="flex items-center gap-2 text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}><Icon size={14} className="text-accent" /> {title}</span>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ChartSkeleton() {
  return <div className="h-40 animate-pulse rounded-lg" style={{ background: 'var(--border-color)' }} />;
}

function ChartEmpty({ text }: { text: string }) {
  return <div className="py-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>{text}</div>;
}
